/**
 * TanStack Query hook for the learner's per-level library mastery distribution.
 * Backs the CoachCard's hero % and journey bar. Cumulative snapshot (not
 * windowed), so the hook takes no window param and can cache aggressively.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { LibraryDistribution } from '@formency/shared';

export const libraryDistributionKeys = {
  all: ['f', 'library-distribution'] as const,
  me: () => [...libraryDistributionKeys.all, 'me'] as const,
};

/**
 * Exponential-backoff schedule for the snapshot stale-flag refetch loop.
 * When the API returns `stale: true` (a session completed since the last
 * snapshot), we poll until the worker job lands and the timestamps match.
 * Capped at this many polls — after which we stop and let the user
 * manually refresh. Roughly 60s total in the worst case.
 */
const STALE_POLL_INTERVALS_MS = [2000, 4000, 8000, 15000, 30000];

/**
 * Fetch the library mastery distribution for the current user.
 *
 * Invalidate `libraryDistributionKeys.all` when:
 *   - a session completes (mastery may have moved a leaf up/down a tier)
 *   - the learner adds or removes a lesson (library composition changed)
 *
 * Auto-refetches while `data.stale === true` using an exponential schedule
 * so the dashboard catches up to the worker's snapshot refresh without the
 * user reloading the page.
 */
export function useMyLibraryDistribution(options?: { enabled?: boolean; staleTime?: number }) {
  return useQuery<LibraryDistribution>({
    queryKey: libraryDistributionKeys.me(),
    queryFn: () => api.get<LibraryDistribution>('/me/library-distribution'),
    staleTime: options?.staleTime ?? 5 * 60_000,
    enabled: options?.enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data?.stale) return false;

      // Number of stale-state refetches so far. dataUpdateCount is 1 after
      // the initial fetch; subtract that, clamp at zero, cap at the schedule
      // length (after which we give up rather than poll forever).
      const staleSoFar = Math.max(0, query.state.dataUpdateCount - 1);
      if (staleSoFar >= STALE_POLL_INTERVALS_MS.length) return false;
      return STALE_POLL_INTERVALS_MS[staleSoFar];
    },
  });
}
